import { Download, Edit, FolderPlus, MoreVertical, Share, Sparkles, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ConfirmDialog } from "../ConfirmDialog";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import React from "react";

interface FolderActionMenuProps {
  /** Called when create subfolder is selected */
  onCreateSubfolder?: () => void;
  /** Called when rename is selected, once the menu has closed (so an inline
   *  editor opened here can take focus without the menu stealing it back) */
  onRename?: () => void;
  /** Called when delete is selected */
  onDelete?: () => void;
  /** Called when export is selected */
  onExport?: () => void;
  /** Called when "Organize with AI" is selected (WHA-3654) */
  onOrganize?: () => void;
  /** Called when share is selected */
  onShare?: () => void;
  /** Label for delete action */
  deleteLabel?: string;
  /** Additional CSS classes */
  className?: string;
}

/**
 * FolderActionMenu - Molecule component
 *
 * Dropdown menu with folder operations (create, rename, delete, export, share).
 * Composed from Button and DropdownMenu UI components.
 * Part of the atomic design system for folder tree display.
 *
 * @example
 * ```tsx
 * <FolderActionMenu
 *   onCreateSubfolder={handleCreate}
 *   onRename={handleRename}
 *   onDelete={handleDelete}
 *   deleteLabel="Archive"
 * />
 * ```
 */
export const FolderActionMenu = React.memo(function FolderActionMenu({
  onCreateSubfolder,
  onRename,
  onDelete,
  onExport,
  onOrganize,
  onShare,
  deleteLabel = "Delete",
  className,
}: FolderActionMenuProps) {
  const { t } = useLanguage();
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);

  // Rename opens the row's inline editor, which focuses itself as it mounts.
  // Running it from the item's onClick puts that editor on screen while the menu
  // is still open, so Radix's focus trap yanks focus straight back out of it —
  // and the editor treats losing focus as "confirm", so it vanishes the instant
  // it appears (leaving the name unchanged). Start the rename once the menu has
  // closed instead, and keep Radix from restoring focus to the trigger so the
  // editor keeps it. Every other action still returns focus to the trigger.
  const renameRequested = React.useRef(false);

  const handleMenuItemClick = (e: React.MouseEvent, action?: () => void) => {
    e.stopPropagation();
    action?.();
  };

  const handleRenameClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    renameRequested.current = true;
  };

  const handleCloseAutoFocus = (e: Event) => {
    if (!renameRequested.current) return;
    renameRequested.current = false;
    e.preventDefault();
    onRename?.();
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteConfirm(true);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "h-6 w-6 p-0 transition-all duration-200",
              "hover:bg-slate-100 hover:shadow-subtle",
              "active:scale-95",
              className
            )}
            onClick={(e) => e.stopPropagation()}
            aria-label={t.documents.folder.folderActions}
          >
            <MoreVertical className="h-3.5 w-3.5 text-slate-500" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="min-w-[180px] shadow-lg"
          onCloseAutoFocus={handleCloseAutoFocus}
        >
          {onCreateSubfolder && (
            <DropdownMenuItem
              onClick={(e) => handleMenuItemClick(e, onCreateSubfolder)}
              className="cursor-pointer font-medium text-slate-700 focus:bg-slate-100"
            >
              <FolderPlus className="mr-2 h-4 w-4 text-slate-500" aria-hidden="true" />
              {t.documents.folder.createSubfolder}
            </DropdownMenuItem>
          )}
          {onOrganize && (
            <DropdownMenuItem
              onClick={(e) => handleMenuItemClick(e, onOrganize)}
              className="cursor-pointer font-medium text-slate-700 focus:bg-slate-100"
            >
              <Sparkles className="mr-2 h-4 w-4 text-slate-500" aria-hidden="true" />
              {t.documents.organize.button}
            </DropdownMenuItem>
          )}
          {onExport && (
            <DropdownMenuItem
              onClick={(e) => handleMenuItemClick(e, onExport)}
              className="cursor-pointer font-medium text-slate-700 focus:bg-slate-100"
            >
              <Download className="mr-2 h-4 w-4 text-slate-500" aria-hidden="true" />
              {t.documents.folder.exportFolder}
            </DropdownMenuItem>
          )}
          {onShare && (
            <DropdownMenuItem
              onClick={(e) => handleMenuItemClick(e, onShare)}
              className="cursor-pointer font-medium text-slate-700 focus:bg-slate-100"
            >
              <Share className="mr-2 h-4 w-4 text-slate-500" aria-hidden="true" />
              {t.documents.folder.shareFolder}
            </DropdownMenuItem>
          )}
          {onRename && (
            <DropdownMenuItem
              onClick={handleRenameClick}
              className="cursor-pointer font-medium text-slate-700 focus:bg-slate-100"
            >
              <Edit className="mr-2 h-4 w-4 text-slate-500" aria-hidden="true" />
              {t.documents.folder.rename}
            </DropdownMenuItem>
          )}
          {onDelete && (
            <DropdownMenuItem
              onClick={handleDeleteClick}
              className="cursor-pointer font-medium text-red-600 focus:bg-red-50 focus:text-red-700"
            >
              <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
              {deleteLabel}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {onDelete && (
        <ConfirmDialog
          open={showDeleteConfirm}
          onOpenChange={setShowDeleteConfirm}
          title={deleteLabel}
          description={t.documents.folder.deleteConfirm}
          confirmLabel={t.documents.folder.delete}
          cancelLabel={t.common.cancel}
          variant="destructive"
          onConfirm={() => {
            setShowDeleteConfirm(false);
            onDelete();
          }}
        />
      )}
    </>
  );
});
